import { cancelSubscription } from "@api/subscription";
import useUser from "@hooks/useUser";
import ExitIcon from "@mui/icons-material/ExitToApp";
import {
  Button,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DateUtil } from "src/util/DateUtil";
import { useSubscriptionContext } from "../../auth/userSubscriptionProvider";
import { useTokenContext } from "../../auth/UserTokenProvider";

interface SettingsPageProps {
  onThemeToggle: () => void;
  isDarkMode: boolean;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ onThemeToggle, isDarkMode }) => {
  const navigate = useNavigate();
  const { clearToken } = useTokenContext();
  const { subscriptionData, setSubscriptionData } = useSubscriptionContext();
  const { user: spotifyUser } = useUser();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleDarkModeToggle = () => {
    onThemeToggle();
  };

  const handleLogout = async () => {
    if (!window.confirm("Are you sure you want to logout?")) return;

    setIsLoggingOut(true);
    try {
      await clearToken();
      navigate("/");
    } catch (error) {
      console.error("Failed to log out:", error);
      toast.error("Could not log out. Please check your connection and try again.");
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleUnsubscribe = async () => {
    const data = await cancelSubscription();
    navigate("/");
    setSubscriptionData(data);
  };

  const isSubscribed = () => {
    return subscriptionData?.subscriptionStatus === "active";
  };

  const subscriptionStatus = () => {
    if (!subscriptionData) {
      return "N/A";
    }
    if (subscriptionData?.subscriptionStatus === "active") {
      return "Subscribed";
    }
    if (subscriptionData?.subscriptionStatus === "canceled") {
      return (
        "Canceled (active until " +
        DateUtil.formattedExpirationDate(subscriptionData.subscriptionEndDate) +
        ")"
      );
    }
    return `Trial (Expires on ${DateUtil.formattedExpirationDate(subscriptionData.trial_ends_at)})`;
  };

  const userName = () => {
    if (!spotifyUser?.display_name) {
      return "N/A";
    }
    if (subscriptionData?.isAdmin) {
      return spotifyUser?.display_name + "(Admin)";
    }
    return spotifyUser?.display_name;
  };

  function openCheckout(event: any): void {
    event.preventDefault();
    navigate("/payment");
  }

  return (
    <div style={{ textAlign: "center", padding: "20px" }}>
      <Typography variant="h3">Settings</Typography>
      <TableContainer component={Paper} style={{ marginTop: "20px" }}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell colSpan={2}>{userName()}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Subscription Status</TableCell>
              <TableCell>{subscriptionStatus()}</TableCell>
              {isSubscribed() && (
                <TableCell>
                  <Button variant="contained" color="secondary" onClick={handleUnsubscribe}>
                    Unsubscribe
                  </Button>
                </TableCell>
              )}
              {!isSubscribed() && (
                <TableCell>
                  <Button variant="contained" color="primary" onClick={openCheckout}>
                    Activate Now
                  </Button>
                </TableCell>
              )}
            </TableRow>

            <TableRow>
              <TableCell>Dark Mode</TableCell>
              <TableCell colSpan={2}>
                <Switch checked={isDarkMode} onChange={handleDarkModeToggle} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <IconButton disabled={isLoggingOut} onClick={handleLogout} style={{ marginTop: "20px" }}>
        <ExitIcon />
        Logout
      </IconButton>
    </div>
  );
};

export default SettingsPage;
